import { useMutation } from "@apollo/client"
import cn from "classnames"
import { type TFunction } from "i18next"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import SVGInline from "react-svg-inline"
import { fixmethodTranslationKey } from "@/admin/components/orders/articles/custom_products_modal"
import { graphql } from "@/api"
import { type CheckoutStateQuery, type OrderItem } from "@/api/graphql"
import ItemCount from "@/checkout/components/basket/item_count"
import ClientData from "@/common/client_data"
import AddBundleToCart from "@/common/components/add_bundle_to_cart"
import AddOrderItemToCart from "@/common/components/add_order_item_to_cart"
import ConfirmLink from "@/common/components/confirm_link"
import { useSignPreviewContext } from "@/common/components/sign_preview"
import env from "@/common/env"
import useMedia from "@/common/hooks/use_media"
import { sizeUnit } from "@/common/locales"
import { Routemap } from "@/common/routes"
import tailwindTheme from "@/common/styles/theme.module.css"
import { basketItemsAddToGA4, isBlank, toggleScroll } from "@/common/util"
import Calc from "@/common/util/calculator"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import { TrashFill, EditFill, BundleOutline, InfoFill, CartOutline } from "@/skylticons"

export type BasketItem = CheckoutStateQuery["order"]["basketItems"][0]

const basketAddAllDoc = graphql(`
  mutation OrderItemsBasketAdd($orderId: Int!) {
    orderItemsBasketAdd(orderId: $orderId) {
      success
      landingUrl

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const editLink = (t: TFunction, item: BasketItem) => {
  if (item.exampleFixedPrice) {
    if (confirm(t("fixed_price.dialog_text")) == true) {
      window.location.href = `/order/edit_id/${item.id}`
    }
  } else {
    window.location.href = `/order/edit_id/${item.id}`
  }
}

export const productName = (t: TFunction, item: BasketItem, infoPage?: boolean) => {
  const name = []

  if (item.additionalProduct) {
    name.push(item.clientDescription)
  } else if (item.metaProduct) {
    name.push(t(`sign_type.${item.metaProduct}`))
  } else {
    name.push(t(`sign_type.${item.signType}`))
  }

  if (infoPage && item.itemNumber) {
    name.unshift(item.itemNumber)
  }

  if (item.signType === "rollup") {
    name.push(t(`material_type.${item.material}`))
  }

  return name.join(" - ")
}

export const fixmethodName = (
  item: Pick<OrderItem, "customProductAttributes" | "customProduct" | "hangType">,
  t: TFunction
) => {
  const fixmethod = item.customProductAttributes?.fixmethod
  const fixmethodCustom = item.customProductAttributes?.fixmethod_custom

  if (item.customProduct) {
    if (isBlank(fixmethodCustom) && isBlank(fixmethod)) {
      return ""
    }

    if (isBlank(fixmethodCustom)) {
      return t(`hang_type.${fixmethodTranslationKey(item.customProductAttributes?.fixmethod)}`)
    } else {
      return fixmethodCustom
    }
  } else {
    if (isBlank(item.hangType || "")) {
      return t("hang_type.none")
    }
  }

  return t(`hang_type.${item.hangType}`)
}

interface SignPreviewWidgetProps {
  item: BasketItem
}

const SignPreviewWidget: React.FC<SignPreviewWidgetProps> = ({ item }) => {
  const { open } = useSignPreviewContext()

  if (!(item.v2 || item.bundleItem)) {
    return null
  }

  const svg =
    item.signType === "double_sided_sign" ? [item.previewSvg || "", item.previewSvgB || ""] : item.previewSvg || ""
  const bundleSvg = item.bundleItem && item.previewSvg

  return (
    <div className="signimage">
      {(item.v2 || bundleSvg) && (
        <div
          id={`sign_${item.id}`}
          className={cn("signv2 signpreview_link", item.signType, item.previewClass)}
          data-type={item.signType}
          data-id={item.id}
          data-background
          data-height={item.height}
          data-width={item.width}
          onClick={() =>
            open({
              svg,
              transparent: true,
              withBackground: true,
              signWidth: item.width || 0,
              signHeight: item.height || 0,
              signType: item.signType || "",
            })
          }
        >
          {item.previewSvgB ? (
            <>
              <SVGInline component="div" svg={item.previewSvg || ""} className="sign_wrap side-a data-hj-suppress" />
              <SVGInline component="div" svg={item.previewSvgB} className="sign_wrap side-b data-hj-suppress" />
            </>
          ) : (
            <SVGInline component="div" svg={item.previewSvg || ""} className="sign_wrap data-hj-suppress" />
          )}
        </div>
      )}
      {item.bundleItem && !bundleSvg && (
        <div className="bundle-item">
          {item.bundle?.image?.png ? <img src={item.bundle.image.png} /> : <BundleOutline />}
        </div>
      )}
    </div>
  )
}

interface ItemProps {
  order: CheckoutStateQuery["order"]
  item: BasketItem
  index: number
  even?: boolean
  onCountUpdate?: (method: string) => void
  infoPage?: boolean
}

const formatSize = (item: BasketItem, separator: string, infoPage?: boolean) => {
  let formattedSize = `${item.width || 0}x${item.height || 0} ${sizeUnit}`

  if (item.width === null && item.height === null) {
    return (formattedSize = "")
  }

  if (infoPage) {
    let thickness = item.thickness

    if (thickness && sizeUnit === "in") {
      const signType = item.signType

      let decimals = 2
      if (signType === "sticker") {
        decimals = 4
      } else if (signType === "rollup") {
        decimals = 3
      }

      thickness = Calc.mmToIn(thickness, decimals)

      if (thickness === 0) {
        thickness = 0.1
      }
    }

    if (thickness) {
      formattedSize = `${item.width || 0}x${item.height || 0}x${thickness
        .toString()
        .replace(".", separator)} ${sizeUnit}`
    }
  }

  return formattedSize
}

const MobileSignCode: React.FC<ItemProps> = ({ order, item, index, even, onCountUpdate, infoPage }) => {
  const { t } = useTranslation()

  const separator = env.TEST ? "." : (t("locale.separator") as string)
  const formattedSize = formatSize(item, separator, infoPage)
  const admin = !!(ClientData.admin && ClientData.admin.id)
  const editable = item.customerEdit || admin || item.signType === "tactile_sign"
  const switchBackground =
    (item.signType === "vinyl_text" || item.signType === "contour_cut") && item.colors === "White"

  return (
    <div key={item.id} className={cn(`signitem product-row mobile previewitem-${item.id}`, { even })}>
      <div className="index">{index}</div>

      <div className="details">
        <div className={cn("sign signimage", { "bg-white-10": switchBackground })} data-id={item.id}>
          <SignPreviewWidget item={item} />
        </div>
        <div className="description">
          <div className="title">
            <div className="name">{item.bundleItem ? item.description : productName(t, item, infoPage)}</div>
            <div className="total">{ClientData.businessRole ? item.localTotal : item.localTaxedTotal}</div>
          </div>

          <div className="flex">
            <div className="properties flex-1">
              {item.previewSvgB && <div className="double-sided">{item.materialTr}</div>}
              <div className="size">{!item.bundleItem && formattedSize}</div>
              <div className="fixmethod">{!item.bundleItem && fixmethodName(item, t)}</div>
              <div className="color">{item.colors}</div>
            </div>
            <div>
              {item.hasDiscount && (
                <div className="text-right text-xs text-ink-50 line-through">
                  {ClientData.businessRole ? item.regularLocalTotal : item.regularTaxedTotal}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="controls">
        <div className="count">
          <ItemCount order={order} item={item} onCountUpdate={onCountUpdate} />
        </div>

        <div className="item-buttons">
          {order.customerEdit && !item.discontinued && (
            <>
              {editable && !item.bundleItem && !item.campaign && (
                <a onClick={() => editLink(t, item)} className="edit_sign_link">
                  {t("misc.edit")}
                  <EditFill />
                </a>
              )}
              <ConfirmLink href={`/order/remove_id/${item.id}`} className="remove_sign_link">
                {t("misc.delete")}
                <TrashFill />
              </ConfirmLink>
            </>
          )}
          {!order.customerEdit &&
            item.v2 &&
            !item.bundleItem &&
            !ClientData.hideUpdateButtons &&
            !item.discontinued && (
              <AddOrderItemToCart orderItemId={parseInt(item.id)} classes="button-small" copyBtn={editable} />
            )}
        </div>
      </div>
    </div>
  )
}

const SignCode: React.FC<ItemProps> = ({ order, item, index, even, onCountUpdate, infoPage }) => {
  const { t } = useTranslation()

  const separator = env.TEST ? "." : (t("locale.separator") as string)
  const formattedSize = formatSize(item, separator, infoPage)
  const admin = !!(ClientData.admin && ClientData.admin.id)
  const editable = item.customerEdit || admin || item.signType === "tactile_sign"
  const switchBackground =
    (item.signType === "vinyl_text" || item.signType === "contour_cut") && item.colors === "White"

  return (
    <tr key={item.id} className={cn(`signitem product-row previewitem-${item.id}`, { even })}>
      <td>{index}</td>
      <td className={cn("sign", { "bg-white-10": switchBackground })}>
        <SignPreviewWidget item={item} />
      </td>
      <td className="name">
        {item.bundleItem ? item.description : productName(t, item, infoPage)}
        {item.previewSvgB && (
          <>
            <br />
            {item.materialTr}
          </>
        )}
      </td>
      <td className="basket_size">{!item.bundleItem && formattedSize}</td>
      <td className="max-w-[195px] truncate" title={fixmethodName(item, t)}>
        {!item.bundleItem && <>{fixmethodName(item, t)}</>}
      </td>
      <td>{item.colors}</td>
      <td className={`basket_item_price_${item.id} prices`}>
        {ClientData.businessRole ? item.localPrice : item.localTaxedPrice}
      </td>
      <td className="count">
        <ItemCount order={order} item={item} onCountUpdate={onCountUpdate} />
      </td>
      <td className={`basket_item_total_price_${item.id} prices`}>
        <div className="paragraph-sm mb-0 text-right font-semibold">
          {ClientData.businessRole ? item.localTotal : item.localTaxedTotal}
        </div>
        {item.hasDiscount && (
          <div className="text-right text-xs text-ink-50 line-through">
            {ClientData.businessRole ? item.regularLocalTotal : item.regularTaxedTotal}
          </div>
        )}
      </td>
      <td className="controls">
        {order.customerEdit && !ClientData.hideUpdateButtons && !item.discontinued && (
          <div className="item-buttons">
            {editable && !item.bundleItem && !item.campaign && (
              <a onClick={() => editLink(t, item)} className="edit_sign_link">
                <EditFill />
                {t("misc.edit")}
              </a>
            )}
            <ConfirmLink href={`/order/remove_id/${item.id}`} className="remove_sign_link">
              <TrashFill />
              {t("misc.delete")}
            </ConfirmLink>
          </div>
        )}
        {!order.customerEdit && !ClientData.hideUpdateButtons && !item.discontinued && !order.paused && (
          <>
            {item.bundleItem && item.bundle ? (
              <AddBundleToCart bundleId={item.bundle?.id} className="button-emphasis mb-0" copyBtn={editable} />
            ) : (
              item.v2 && (
                <AddOrderItemToCart orderItemId={parseInt(item.id)} classes="button-small mb-0" copyBtn={editable} />
              )
            )}
          </>
        )}
      </td>
    </tr>
  )
}

const AdditionalProduct: React.FC<ItemProps> = ({ order, item, index, even, onCountUpdate, infoPage }) => {
  const { t } = useTranslation()
  const separator = env.TEST ? "." : (t("locale.separator") as string)
  const formattedSize = formatSize(item, separator, infoPage)

  return (
    <tr key={item.id} className={cn(`previewitem-${item.id}`, { even })}>
      <td>{index}</td>
      <td />
      <td className="name">{productName(t, item, infoPage)}</td>
      <td className="basket_size">{formattedSize}</td>
      <td>{fixmethodName(item, t)}</td>
      <td>{item.colors}</td>
      <td>{item.localPrice}</td>
      <td className="count">
        <ItemCount order={order} item={item} onCountUpdate={onCountUpdate} />
      </td>
      <td>
        <div className="paragraph-sm mb-0 text-right font-semibold">{item.localTotal}</div>
      </td>
      <td className="controls">
        {order.customerEdit && (
          <div className="item-buttons">
            <ConfirmLink href={`/order/remove_id/${item.id}`} className="remove_sign_link">
              <TrashFill />
              {t("misc.delete")}
            </ConfirmLink>
          </div>
        )}
      </td>
    </tr>
  )
}

const MobileAdditionalProduct: React.FC<ItemProps> = ({ order, item, index, even, onCountUpdate, infoPage }) => {
  const { t } = useTranslation()

  return (
    <div key={item.id} className={cn(`signitem product-row mobile previewitem-${item.id}`, { even })}>
      <div className="index">{index}</div>

      <div className="details">
        <div className="sign" />
        <div className="description">
          <div className="title">
            <div className="name">{productName(t, item, infoPage)}</div>
            <div className="total">{ClientData.businessRole ? item.localPrice : item.localTotal}</div>
          </div>
        </div>
      </div>

      <div className="controls">
        <div className="count">
          <ItemCount order={order} item={item} onCountUpdate={onCountUpdate} />
        </div>
        <div className="item-buttons">
          {order.customerEdit && (
            <ConfirmLink href={`/order/remove_id/${item.id}`} className="remove_sign_link">
              {t("misc.delete")}
              <TrashFill />
            </ConfirmLink>
          )}
        </div>
      </div>
    </div>
  )
}

const createItem = (mobile: boolean, item: BasketItem): React.FC<ItemProps> => {
  if (item.articleType === "additional_product") {
    return mobile ? MobileAdditionalProduct : AdditionalProduct
  }

  return mobile ? MobileSignCode : SignCode
}

interface TableProps {
  order: CheckoutStateQuery["order"]
  onCountUpdate?: (method: string) => void
  infoPage?: boolean
}

export const MobileTable: React.FC<TableProps> = ({ order, onCountUpdate, infoPage }) => (
  <div className="mobiletable lg:hidden">
    {order.basketItems?.map((item, i) =>
      React.createElement(createItem(true, item), {
        key: i,
        index: item.productIndex || 0,
        even: i % 2 === 0,
        order,
        item,
        onCountUpdate,
        infoPage,
      })
    )}
  </div>
)

export const DesktopTable: React.FC<TableProps> = ({ order, onCountUpdate }) => {
  const { t } = useTranslation()

  return (
    <>
      {order.confirmed && <ItemsHeader order={order} />}
      <table className="ordertable hidden w-full max-w-full lg:table">
        <thead>
          <tr>
            <th>{t("signslist.number")}</th>
            <th>{t("signslist.sign")}</th>
            <th>{t("article.product_type")}</th>
            <th>{t("sign.dimensions")}</th>
            <th>{t("article.fix_method")}</th>
            <th>{t("article.color")}</th>
            <th>{t("article.price")}</th>
            <th>{t("article.count")}</th>
            <th className="text-right">{t("article.total")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {order.basketItems?.map((item, i) =>
            React.createElement(createItem(false, item), {
              key: i,
              index: item.productIndex || 0,
              even: i % 2 === 0,
              order,
              item,
              onCountUpdate,
              infoPage: true,
            })
          )}
        </tbody>
      </table>
    </>
  )
}

interface ItemsHeaderProps {
  order: CheckoutStateQuery["order"]
}

const ItemsHeader: React.FC<ItemsHeaderProps> = ({ order }) => {
  const { t } = useTranslation()
  const [setBasketState] = useMutation(setBasketStateDoc)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensXl})`)
  const anyOrderableItems = order.basketItems.some(item => !item.discontinued)

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })

    if (!desktop) {
      toggleScroll(true)
    }
  }, [setBasketState, desktop])

  const [addMutation, addMutationResult] = useMutation(basketAddAllDoc, {
    update: (store, { data: orderItemsBasketAddData }) => {
      if (orderItemsBasketAddData?.orderItemsBasketAdd?.success) {
        const order = orderItemsBasketAddData.orderItemsBasketAdd.status
        basketItemsAddToGA4(order, orderItemsBasketAddData.orderItemsBasketAdd.landingUrl)
        openBasket()
      }
    },
  })

  const addBasket = useCallback(() => {
    addMutation({ variables: { orderId: Number(order.id) } })
  }, [addMutation, order.id])

  const loading = addMutationResult.loading

  return (
    <div className="mb-4 flex flex-col justify-between md:mb-6 lg:flex-row">
      <div className="mb-4 flex flex-col items-center justify-start sm:mb-0 sm:flex-row sm:items-start">
        {anyOrderableItems && !order.paused && (
          <button
            id="copy_all_to_cart"
            className="button button-secondary button-medium w-full sm:mr-2 sm:w-auto lg:mb-0"
            onClick={() => addBasket()}
            disabled={loading}
            type="button"
            data-gtm="copy-all-to-cart"
          >
            <CartOutline />
            <span>{t("order.copy_all_to_cart")}</span>
          </button>
        )}
        <a
          id="show_previous_orders"
          className="button button-secondary button-medium mb-0 ml-0 w-full sm:w-auto"
          href={Routemap.customerZone(t)}
        >
          {t("order.show_previous_orders")}
        </a>
      </div>
      {!order.customerEdit && !ClientData.hideUpdateButtons && anyOrderableItems && !order.paused && (
        <div className="order_again flex rounded border bg-info p-3 lg:ml-4 lg:max-w-md">
          <InfoFill className="mr-2 h-6 shrink-0 items-start text-info" />
          <p className="paragraph-sm mb-0">{t("order.edit_after_copy")}</p>
        </div>
      )}
    </div>
  )
}
